// DireccionRptVacaciones.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Card,
  Form,
  Row,
  Col,
  Spinner,
} from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import * as XLSX from "xlsx";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import CatVacaciones from '../../../Services/Catalogos/CatVacaciones';
import { EmpleadoVacacionesDTO } from './DTO/EmpleadoVacacionesDTO'; // Asegúrate de que la ruta sea correcta
import { MsgInformativo } from '../../../Utils/Toast/msgInformativo';

const DireccionRptVacaciones: React.FC = () => {
  const [concentradoVacaciones, setConcentradoVacaciones] = useState<EmpleadoVacacionesDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtroConcentrado, setFiltroConcentrado] = useState('');

  const [showMsg, setShowMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [header, setHeader] = useState('');

  // Cargar concentrado al montar el componente
  useEffect(() => {
    obtenerConcentradoVacaciones();
  }, []);

  const obtenerConcentradoVacaciones = async () => {
    setLoading(true);
    try {
      const response = await CatVacaciones.getAllConcentradoVacaciones();
      
      if (response.data.length === 0) {
        setMsg('No hay datos de vacaciones disponibles en este momento.');
        setHeader('Informativo');
        setShowMsg(true);
      } else {
        setConcentradoVacaciones(response.data);
      }
    } catch (error) {
      console.error('Error al cargar concentrado de vacaciones:', error);
      setHeader('Error');
      setMsg('No se pudo cargar el concentrado de vacaciones.');
      setShowMsg(true);
    } finally {
      setLoading(false);
    }
  };

  // Filtrado en tiempo real
  const concentradoFiltrado = useMemo(() => {
    if (!filtroConcentrado.trim()) return concentradoVacaciones;

    const search = filtroConcentrado.toLowerCase().trim();
    return concentradoVacaciones.filter(e =>
      `${e.numEmpleado} ${e.nombre}`.toLowerCase().includes(search)
    );
  }, [concentradoVacaciones, filtroConcentrado]);

  // Columnas de la tabla
  const columnsConcentradoVacaciones = [
    { name: 'NumEmpleado', selector: (row: EmpleadoVacacionesDTO) => row.numEmpleado, sortable: true, width: '130px' },
    { name: 'Nombre', selector: (row: EmpleadoVacacionesDTO) => row.nombre, sortable: true, width: '220px' },
   


    {
    name: 'Fecha Ingreso',
    selector: (row: EmpleadoVacacionesDTO) => formatearFecha(row.fechaIngreso),
    sortable: true,
    width: '150px',
  },
  {
    name: 'Próximo Aniversario',
    selector: (row: EmpleadoVacacionesDTO) => formatearFecha(row.proximoAniversario),
    sortable: true,
    width: '200px',
  },
    { name: 'Años Servicio', selector: (row: EmpleadoVacacionesDTO) => row.anosServicio, sortable: true, width: '150px' },
    { name: 'Días Vacaciones', selector: (row: EmpleadoVacacionesDTO) => row.diasPermitidos, sortable: true, width: '170px' },
    { name: 'Días Tomados', selector: (row: EmpleadoVacacionesDTO) => row.diasTomados, sortable: true, width: '150px' },
    { name: 'Días Pagados', selector: (row: EmpleadoVacacionesDTO) => row.diasYaPagados, sortable: true, width: '150px' },
    { name: 'Días Restantes', selector: (row: EmpleadoVacacionesDTO) => row.diasRestantes, sortable: true, width: '130px' },
    { name: 'Días por Pagar', selector: (row: EmpleadoVacacionesDTO) => row.diasPorPagar, sortable: true, width: '150px' },
  ];

  // Formateo de fechas (reutilizamos tu función)
 const formatearFecha = (fecha?: string | null | undefined): string => {
  // Si no hay fecha o es vacía/inválida → devolvemos un placeholder limpio
  if (!fecha || typeof fecha !== 'string' || fecha.trim() === '') {
    return '—';
  }

  try {
    // Creamos la fecha y verificamos que sea válida
    const date = new Date(fecha);
    if (isNaN(date.getTime())) {
      return '—'; // Fecha inválida (ej: "abc", "0000-00-00")
    }

    const dia = date.getDate().toString().padStart(2, '0');
    const mes = (date.getMonth() + 1).toString().padStart(2, '0');
    const anio = date.getFullYear();

    return `${dia}/${mes}/${anio}`;
  } catch (error) {
    console.warn('Error formateando fecha:', fecha, error);
    return '—';
  }
};

  // Exportar a Excel (igual que en el componente original)
  
 

const downloadExcelConcentrado = async () => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Concentrado Vacaciones', {
    properties: { defaultColWidth: 15, defaultRowHeight: 18 },
  });

  const nombreArchivo = `Concentrado_Vacaciones_${new Date().toLocaleDateString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })}.xlsx`;

  // 1. LOGO GEMCO (igual que tu ejemplo)
  try {
    const response = await fetch('/GEMCO.png');
    if (response.ok) {
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const logoId = workbook.addImage({
        buffer: arrayBuffer,
        extension: 'png',
      });
      worksheet.addImage(logoId, {
        tl: { col: 0.5, row: 0.5 },
        ext: { width: 140, height: 80 },
      });
    }
  } catch (e) {
    console.warn("Logo no encontrado");
  }

  // 2. Títulos grandes
  worksheet.mergeCells('B2:I2');
  const title = worksheet.getCell('B2');
  title.value = 'GLAFIRO E. MONTEMAYOR Y CIA S.C.';
  title.font = { bold: true, size: 18, color: { argb: 'FF000000' } };
  title.alignment = { horizontal: 'center', vertical: 'middle' };

  worksheet.mergeCells('B3:I3');
  const subtitle = worksheet.getCell('B3');
  subtitle.value = 'CONCENTRADO DE VACACIONES - DIRECCIÓN';
  subtitle.font = { bold: true, size: 14, color: { argb: 'FF1A3C6D' } };
  subtitle.alignment = { horizontal: 'center', vertical: 'middle' };

  // Fecha de generación
  worksheet.getCell('B5').value = 'Generado el:';
  worksheet.getCell('C5').value = new Date().toLocaleString('es-MX', {
    dateStyle: 'long',
    timeStyle: 'short',
  });
  worksheet.getCell('C5').font = { italic: true, size: 10 };

  // 3. Encabezados (fila 8)
  const headerRow = 8;
  const headers = [
    'Num. Empleado',
    'Nombre',
    'Fecha Ingreso',
    'Próximo Aniversario',
    'Años Servicio',
    'Días Vacaciones',
    'Días Tomados',
    'Días Pagados',
    'Días Restantes',
    'Días por Pagar',
  ];

  headers.forEach((header, index) => {
    const cell = worksheet.getCell(headerRow, index + 2); // Empieza en columna B
    cell.value = header;
    cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1A3C6D' } };
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  // 4. Datos (a partir de fila 9)
  let currentRow = headerRow + 1;
  concentradoFiltrado.forEach((emp) => {
    const row = worksheet.getRow(currentRow);

    row.values = [
      null, // margen A
      emp.numEmpleado,
      emp.nombre,
      formatearFecha(emp.fechaIngreso),
      formatearFecha(emp.proximoAniversario),
      emp.anosServicio,
      emp.diasPermitidos,
      emp.diasTomados,
      emp.diasYaPagados,
      emp.diasRestantes,
      emp.diasPorPagar,
    ];

    // WrapText en columnas largas (Nombre, etc.)
    row.getCell(3).alignment = { wrapText: true, vertical: 'middle' }; // Nombre

    // Color condicional en "Días Restantes" bajos
   

    // Bordes en toda la fila
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber >= 2) {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
      }
    });

    currentRow++;
  });

  // 5. Ajuste automático de ancho de columnas (mejorado)
  

  // 6. Leyenda al final
  const leyendaRow = currentRow + 3;
  
  worksheet.getCell(leyendaRow + 2, 2).value = 'Generado desde sistema RH - Dirección';
  worksheet.getCell(leyendaRow + 2, 2).font = { italic: true, size: 9 };

  // 7. Descargar
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, nombreArchivo);
};



  

  return (
    <div className="p-4">
      <Card className="shadow-sm border-0">
        {/* <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Reporte de Vacaciones - Dirección</h5>
          <Button variant="light" size="sm" onClick={obtenerConcentradoVacaciones}>
            Actualizar
          </Button>
        </Card.Header> */}

        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Cargando concentrado...</p>
            </div>
          ) : concentradoVacaciones.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No hay datos de vacaciones disponibles.
            </div>
          ) : (
            <>
              {/* Filtro y exportación */}
              <Row className="mb-3 align-items-end">
                <Col md={6} lg={4}>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por nombre o número de empleado..."
                    value={filtroConcentrado}
                    onChange={(e) => setFiltroConcentrado(e.target.value)}
                  />
                </Col>
                <Col md="auto" className="ms-auto">
                  <Button variant="success" onClick={downloadExcelConcentrado}>
                    Exportar a Excel
                  </Button>
                </Col>
              </Row>

              <DataTable
                columns={columnsConcentradoVacaciones}
                data={concentradoFiltrado}
                pagination
                paginationPerPage={15}
                paginationRowsPerPageOptions={[10, 15, 25, 50]}
                dense
                striped
                highlightOnHover
                pointerOnHover
                responsive
                noDataComponent="No se encontraron registros con ese filtro"
                customStyles={{
                  headCells: {
                    style: {
                      backgroundColor: '#f8f9fa',
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                    },
                  },
                  cells: {
                    style: {
                      fontSize: '0.85rem',
                    },
                  },
                }}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {/* Toast informativo */}
      <MsgInformativo
        show={showMsg}
        header={header}
        msg={msg}
        msgColor={header === 'Error' ? 'danger' : 'primary'}
        closeToast={() => setShowMsg(false)}
      />
    </div>
  );
};

export default DireccionRptVacaciones;